import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import "./data-finder.css";
import "./data-links.css";

import { Context } from "../../models/context";
import Papa from "papaparse";

type Category =
    | "PrintCurrentDataframe"
    | "SaveCurrentDataframe"
    | "LoadSwitchCurrentDataframe"
    | "RestoreOriginalDataframe"
    | "DataframeManipulation"
    | "PidScraping"
    | "ArkScraping"
    | "AddTool"
    | "OtherFunctions"
    | "YellowTemple";

const routes: Record<Category, string> = {
    PrintCurrentDataframe: "/data-finder/print-current-dataframe",
    SaveCurrentDataframe: "/data-finder/save-current-dataframe",
    LoadSwitchCurrentDataframe: "/data-finder/load-switch-current-dataframe",
    RestoreOriginalDataframe: "/data-finder/restore-original-dataframe",
    DataframeManipulation: "/data-finder/manipulation",
    PidScraping: "/data-finder/pid-scraping",
    ArkScraping: "/data-finder/ark-scraping",
    AddTool: "/data-finder/add-tool",
    OtherFunctions: "/data-finder/other-functions",
    YellowTemple: "/data-finder/yellow-temple",
};

const categories: { id: Category; label: string }[] = [
    { id: "PrintCurrentDataframe", label: "Print Current Dataframe" },
    { id: "SaveCurrentDataframe", label: "Save Current Dataframe" },
    { id: "LoadSwitchCurrentDataframe", label: "Load/Switch Current Dataframe" },
    { id: "RestoreOriginalDataframe", label: "Restore Original Dataframe" },
    { id: "DataframeManipulation", label: "Dataframe Manipulation" },
    { id: "PidScraping", label: "Pid Scraping" },
    { id: "ArkScraping", label: "Ark Scraping" },
    { id: "AddTool", label: "Add Tool" },
    { id: "OtherFunctions", label: "Other Functions" },
    { id: "YellowTemple", label: "Yellow Temple" },
];

export default function DataFinder() {
    const context = useContext(Context);
    const navigate = useNavigate();

    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [csvRows, setCsvRows] = useState<string[][]>([]);
    const [csvFileName, setCsvFileName] = useState<string | null>(null);
    const [visibleRows, setVisibleRows] = useState(50);

    function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        setCsvFileName(file.name);
        setVisibleRows(50);

        Papa.parse(file, {
            complete: (result) => {
                setCsvRows(result.data as string[][]);
            },
            skipEmptyLines: true,
        });
    }

    useEffect(() => {
        if (!context.user) {
            navigate("/login");
        }
    }, [context.user, navigate]);

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            const target = e.target as HTMLElement;
            if (
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                e.metaKey || e.ctrlKey || e.altKey
            ) {
                return;
            }

            if (e.key === "Enter") {
                if (selectedCategory) {
                    e.preventDefault();
                    navigate(routes[selectedCategory]);
                }
            }

            let index = -1;
            if (e.key >= "1" && e.key <= "9") {
                index = Number(e.key) - 1;
            } else if (e.key === "0") {
                index = 9;
            }

            if (index >= 0 && index < categories.length) {
                e.preventDefault();
                const id = categories[index].id;
                setSelectedCategory((prev) => (prev === id ? null : id));
            }
        }

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedCategory, navigate]);

    return (
        <div id="data-main">
            <div id="data-box" className="container">
                <div id="data-columns">
                    <div id="data-left">
                        <h1>Welcome to DataFinder!</h1>
                        {/* Link to family search? */}
                        <p id="intro">DataFinder is a tool built to be used in the RLL.
                            You can load in .csv files with ark, PIDs, or hints,
                            and it will query Family Search for birthplaces, birthdates,
                            or any other information found on the Family Search website.
                        </p>
                        <div className="data-link-section">
                            <div className="circle">
                                <div className="circle-num">
                                    <h2>1</h2>
                                </div>
                            </div>
                            <div className="section-heading">
                                <h2>Upload a .csv file</h2>
                                <input type="file" id="file-upload" className="custom-file-input" accept=".csv" onChange={handleFileUpload} />
                            </div>
                        </div>
                        <div className="data-link-section">
                            <div className="circle">
                                <div className="circle-num">
                                    <h2>2</h2>
                                </div>
                            </div>
                            <div className="section-heading">
                                <h2>Select an action/category</h2>
                                <div id="categories">
                                    {categories.map((cat) => (
                                        <div
                                            key={cat.id}
                                            className={`category-box ${selectedCategory === cat.id ? "selected" : ""}`}
                                            onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                                        >
                                            {cat.label}
                                        </div>
                                    ))}
                                </div>
                                <div id="continue-wrapper">
                                    <div id="continue-box" className={`continue-box ${selectedCategory ? "active" : ""}`}
                                        onClick={() => {
                                            if (!selectedCategory) return;
                                            navigate(routes[selectedCategory]);
                                        }}
                                    >Continue</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div id="data-right">
                        {csvRows.length > 0 && (
                            <div className="csv-preview">
                                <h3>{csvFileName}</h3>
                                <div className="csv-preview-scroll">
                                    <table>
                                        <thead>
                                            <tr>
                                                {csvRows[0].map((header, i) => (
                                                    <th key={i}>{header}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {csvRows.slice(1, visibleRows + 1).map((row, i) => (
                                                <tr key={i}>
                                                    {row.map((cell, j) => (
                                                        <td key={j}>{cell}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="csv-preview-controls">
                                    <p className="csv-preview-note">
                                        Showing {Math.min(visibleRows, csvRows.length - 1)} of {csvRows.length - 1} rows
                                    </p>

                                    <button
                                        className="csv-load-more"
                                        onClick={() => setVisibleRows((v) => Math.max(50, v - 50))}
                                        disabled={visibleRows <= 50}
                                    >
                                        Show less ▲
                                    </button>

                                    <button
                                        className="csv-load-more"
                                        onClick={() => setVisibleRows((v) => v + 50)}
                                        disabled={visibleRows >= csvRows.length - 1}
                                    >
                                        Show more ▼
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}